import { Button } from '@warp-ds/react';
import { useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { safeFormatISODate } from 'shared/util/datetime-fns';
import { queryString } from 'shared/util/query';
import { replacePropsWithPreview } from '../../utils';

export interface NextButtonProps {
    adId: number;
    selectedDates: { from: Date | null; to: Date | null };
    numberOfGuests: string;
    isPreview: boolean;
    open: boolean;
    isLoggedIn: boolean;
}

interface RenderHelpTextProps {
    hasDates: boolean;
    noGuestsSelected: boolean;
    errors: { dates?: boolean; guests?: boolean };
    showPrice: boolean;
}

const RenderHelpText = ({ hasDates, noGuestsSelected, errors, showPrice }: RenderHelpTextProps) => {
    switch (true) {
        case !hasDates && errors.dates:
            return <p className="text-12 text-center s-text-negative my-8">Velg datoene du ønsker å leie før du går videre</p>;
        case hasDates && noGuestsSelected && errors.guests:
            return <p className="text-12 text-center s-text-negative my-8">Velg antall gjester før du går videre</p>;
        case showPrice:
            return <p className="text-12 text-center s-text-subtle mt-8">Du betaler ingenting ennå</p>;
        default:
            return null;
    }
};

export const NextButton = ({ adId, selectedDates, numberOfGuests, isPreview, open, isLoggedIn }: NextButtonProps) => {
    const { contextPath } = useConfig();
    const [isInProgress, setInProgress] = useState(false);
    const [errors, setErrors] = useState<{ dates: boolean; guests: boolean }>({ dates: false, guests: false });

    const noGuestsSelected = numberOfGuests === 'Velg antall';
    const hasDates = selectedDates.from !== null && selectedDates.to !== null;
    const showPrice = hasDates && !noGuestsSelected;
    const showLogInButton = !isLoggedIn && showPrice;
    const bookingUrl = `${contextPath}/opprettforesporsel/${queryString({
        finnkode: adId,
        startDate: safeFormatISODate(selectedDates.from),
        endDate: safeFormatISODate(selectedDates.to),
        guests: numberOfGuests,
    })}`;

    const handleButtonClick = (e: { preventDefault: () => void }) => {
        if (!hasDates) {
            e.preventDefault();
            setErrors({ ...errors, dates: true });
        } else if (noGuestsSelected) {
            e.preventDefault();
            setErrors({ ...errors, guests: true });
        } else {
            setInProgress(true);
            // Workaround for sticky progress bar when command-clicking the link:
            setTimeout(() => {
                setInProgress(false);
            }, 3000);
        }
    };

    const previewProps = replacePropsWithPreview({ onClick: handleButtonClick }, isPreview);

    return (
        <div>
            <Button
                primary
                fullWidth
                aria-label={showLogInButton ? 'Logg inn for å gå videre' : 'Gå videre til bestilling'}
                className={`${isInProgress ? 'in-progress' : ''}`}
                href={showLogInButton ? `/auth/login?redirectUrl=${bookingUrl}` : bookingUrl}
                {...previewProps}>
                {showLogInButton ? 'Logg inn for å gå videre' : 'Gå videre'}
            </Button>
            {open && RenderHelpText({ hasDates, noGuestsSelected, errors, showPrice })}
        </div>
    );
};
